<template>
  <div class="row justify-content-center align-items-center" v-if="loginCheckComplete" style="margin-bottom: 50px">
    <div class="card">
      <div class="logo mb-4 text-center">
        <img
            :src="logo"
            class="img-fluid w-50"
            alt="Logo"
        />
      </div>
      <div v-if="showRegistration">
        <div class="info mb-4">
          You do not have an account with LeveragePoint.  Please reach out to
          <a href="mailto:techsupport@leveragepoint.com">techsupport@leveragepoint.com</a> if you have any questions.
        </div>
      </div>

      <div class="form-content text-center">
        <div @submit.prevent="signup" v-if="!codeSent">
          <div class="info mb-4">
            Please enter your email to receive a one-time code from LeveragePoint to Login.
          </div>

          <form class="flex flex-column gap-2">
            <span class="p-float-label mb-3">
              <InputText class="email-input form-control" v-model="email" placeholder="Enter Email" />
            </span>
            <Button :loading="inProgress" @click.prevent="signup" type="submit" label="Submit" class="btn btn-primary" />
          </form>
        </div>

        <div @submit.prevent="checkCode" v-else>
          <div class="info mb-4">
            One-time code sent by LeveragePoint.<br /><br />
            Please check for an email from no-reply@leveragepoint.com, and reach out to
            <a href="mailto:techsupport@leveragepoint.com">techsupport@leveragepoint.com</a>
            if you did not receive it, or if you have any other questions or concerns.
          </div>

          <div v-if="codeInvalid" class="p-error mb-3">
            <i class="pi pi-times"></i> Invalid code. Please try again.
          </div>
          <div v-else-if="codeResent" class="p-success mb-3">
            <i class="pi pi-envelope"></i> Code resent. Please check your email.
          </div>

          <form class="flex flex-column gap-2">
            <span class="p-float-label mb-3">
              <InputText v-model="code" placeholder="Enter Code" />
            </span>
            <Button @click.prevent="checkCode" type="submit" label="Submit" class="btn btn-primary" />
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {defineComponent, inject, onMounted, onBeforeMount, ref, defineEmits} from 'vue';
import {useCognitoConfig, useWhoAmI} from '@/hooks/index'
import AwsCognito from '@/services/AwsCognito'
import CryptoJS from 'crypto-js'
import { useAuthStore } from "@/store"
import VueJwtDecode from 'vue-jwt-decode'
import logo from '@/assets/images/logo-dark.png'
import { useRouter } from 'vue-router';
import {getCognitoInstance} from '@/services/CognitoInstance'
import authHelper from '@/views/Auth/authHelper'

const whoAmI = useWhoAmI()
// const cognitoConfig = useCognitoConfig()
let cognito;

const inProgress = ref(false)
const codeSent = ref(false);
const showRegistration = ref(false)
const email = ref('');
const username = ref('');
const code = ref('');
const pass = ref('');
const codeTries = ref(0);
const codeInvalid = ref(false);
const loginCheckComplete = ref(false);
const codeResent = ref(false);

const router = useRouter();

const $cookies = inject<VueCookies>('$cookies')

const authStore = useAuthStore()

const emit = defineEmits(["update:loginCheckComplete"])

onBeforeMount(async () => {
  cognito = await getCognitoInstance()
  const {checkValidSession} = await authHelper()

  await checkValidSession()

  emit("update:loginCheckComplete", true)
  loginCheckComplete.value = true
})

const setupUser = (data: any) => {
  authStore.loggedInUser = {
    name: data.find(item => item.Name === 'name')?.Value ?? '',
    email: data.find(item => item.Name === 'email')?.Value ?? '',
    configName: data.find(item => item.Name === 'custom:configName')?.Value ?? '',
    roleId: data.find(item => item.Name === 'custom:roleId')?.Value ?? ''
  };
}


const checkCode = async () => {
  console.log('check')
  codeTries.value += 1;

  const [accessToken, refreshToken, idToken] = await cognito.respondToAuthChallenge(email.value, code.value);

  if (accessToken && refreshToken) {

    $cookies.set('lpiTokenRefresh', refreshToken, '30d');
    $cookies.set('lpiTokenAccess', accessToken);
    $cookies.set('lpiJwt', idToken);


    const user = await cognito.getCurrentUser(accessToken);
    setupUser(user.UserAttributes);

    authStore.loggedIn = true

    const targetUrl = authStore.targetUrl || '/home';
    authStore.targetUrl = null
    router.push(targetUrl);

  } else if (codeTries.value <= 3) {
    codeResent.value = false;
    codeInvalid.value = true;
  } else {
    codeTries.value = 0;
    codeInvalid.value = false;
    codeResent.value = true;
  }
};

const signup = async () => {
  inProgress.value = true
  showRegistration.value = false

  const response = await whoAmI.whoAmI(email.value)

  if (response.status === 'confirm') {
    cognito.setSession(response.sessionId)
    codeSent.value = true
  }
  else if (response.status === 'register') {
    showRegistration.value = true
    inProgress.value = false
    // TODO ask the user to register
  } else {
    //TODO: show an unauthorized message
    console.log(response)
  }
};
</script>
<style scoped lang="scss">
@import "rfs/scss";

$space: 1rem;
$gray-100: #fafafa;



a {
  color: #42b983;
}


.card {
  padding: $space * 4;
  background-color: $gray-100;
  box-shadow: 0 0 $space rgba(0, 0, 0, 0.03),
  0 $space ($space * 2) rgba(0, 0, 0, 0.05),
  0 ($space * 2) ($space * 4) rgba(0, 0, 0, 0.05),
  0 ($space * 4) ($space * 8) rgba(0, 0, 0, 0.05);
  @include font-size(1rem);
}




.btn {
  color: white;
  font-weight: 400;
  font-size: 0.9rem;
  margin-top: 15px;
  background-color: #ec6822;
  border-color: transparent;
  border-radius: 8px;
  width: 125px;
  height: 35px;

}

.btn:hover {
  background-color: #0a7c41;
  transition: 0.4s;
  color: white;
}


.info {
  text-align: left;
  margin-bottom: 30px;
}
</style>
